/* global data, leagueData, serverParams */
/* eslint-disable import/no-relative-packages */
import Table from 'bam-node-savant-table/table'
import { guff } from 'bam-node-baseball-savant/utils/guff'

import { populateUrl } from '../utils/leaderboard-filter-helpers'
import { getFilteredData } from '../utils/bat-tracking-table-helpers'

import initStaticHeaders from '../utils/static-headers'

/* eslint-disable-next-line */
import * as viz from '../catcher-stance-viz'
import concatGroupedHeaders from '../utils/concat-grouped-headers'
import getGroupedHeaders from '../utils/get-grouped-headers'
import getDynamicAggregateFirstHeaders from '../utils/dynamic-aggregate-first-headers'

const tableDivId = 'catcher_stance_table'
let showLeagueAverage = false
let expanded = false

const groupHeaders = getGroupedHeaders(serverParams.secondaryGroupBy)

const isLeague = serverParams.type === 'league'

// Follow-up: Add this as a parameterized option to highlightDynamic in Table.js
function getHighlightRange(col) {
    const colData = data.map((d) => d[col])
    const maxRange =
        Math.max(
            Math.abs(Math.max(...colData)),
            Math.abs(Math.min(...colData))
        ) * 0.8

    return [maxRange * -1, 0, maxRange]
}

const link =
    serverParams.type === 'catcher'
        ? '?stats=statcast-r-catching-mlb#statcast-catcher-stance'
        : ''
const firstHeaders = getDynamicAggregateFirstHeaders(serverParams.type, link)

const headers = [
    ...firstHeaders,

    {
        name: 'Tracked<br />Pitches',
        field: 'skeletal_pitches',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'pitch_count'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: 'pct_one_down',
        headerClass: `th-component-header align-center th-vertical-bottom border-left dotted-right`,
        fieldClass: `tr-data align-center border-left`,
        defaultSortDir: 'desc',
        type: 'number',
        highlightDynamic: true,
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Both<br />Up',
        field: 'pct_both_up',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'R Up<br />L Down',
        field: 'pct_l_down',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'L Up<br />R Down',
        field: 'pct_r_down',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Both<br />Down',
        field: 'pct_both_down',
        headerClass: `th-component-header align-center th-vertical-bottom dotted-right`,
        fieldClass: `tr-data align-center dotted-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Extended<br />Leg',
        field: 'pct_leg_extended',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Inside<br />Down',
        field: 'pct_inside_down',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'Knee on same side<br />batter stance',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Outside<br />Down',
        field: 'pct_outside_down',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'Knee on opposite side<br />batter stance',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_framing_162' : 'one_knee_framing',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_framing_162')
            : getHighlightRange('one_knee_framing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_framing_162' : 'other_framing',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_framing_162')
            : getHighlightRange('one_knee_framing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_blocking_162' : 'one_knee_blocking',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_blocking_162')
            : getHighlightRange('one_knee_blocking'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_blocking_162' : 'other_blocking',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_blocking_162')
            : getHighlightRange('one_knee_blocking'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_throwing_162' : 'one_knee_throwing',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_throwing_162')
            : getHighlightRange('one_knee_throwing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_throwing_162' : 'other_throwing',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_throwing_162')
            : getHighlightRange('one_knee_throwing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Total<br />Run Value',
        field: 'total_frv',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_pitching_162' : 'one_knee_pitching',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_pitching_162')
            : getHighlightRange('one_knee_pitching'),
        /* eslint-disable-next-line */
        toolTip: isLeague
            ? 'Per 24,000 Pitches<br />(Catcher-Season)'
            : serverParams.type !== 'pitcher'
            ? `RV of <em>pitchers</em><br />throwing to this ${serverParams.type}`
            : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_pitching_162' : 'other_pitching',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_pitching_162')
            : getHighlightRange('one_knee_pitching'),
        /* eslint-disable-next-line */
        toolTip: isLeague
            ? 'Per 24,000 Pitches<br />(Catcher-Season)'
            : serverParams.type !== 'pitcher'
            ? `RV of <em>pitchers</em><br />throwing to this ${serverParams.type}`
            : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
]

const headersExpanded = [
    ...firstHeaders,

    {
        name: 'Tracked<br />Pitches',
        field: 'skeletal_pitches',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'pitch_count'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: 'pct_one_down',
        headerClass: `th-component-header align-center th-vertical-bottom border-left dotted-right`,
        fieldClass: `tr-data align-center border-left`,
        defaultSortDir: 'desc',
        type: 'number',
        highlightDynamic: true,
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Both<br />Up',
        field: 'pct_both_up',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'R Up<br />L Down',
        field: 'pct_l_down',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'L Up<br />R Down',
        field: 'pct_r_down',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Both<br />Down',
        field: 'pct_both_down',
        headerClass: `th-component-header align-center th-vertical-bottom dotted-right`,
        fieldClass: `tr-data align-center dotted-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Extended<br />Leg',
        field: 'pct_leg_extended',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_framing_162' : 'one_knee_framing',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_framing_162')
            : getHighlightRange('one_knee_framing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_framing_162' : 'other_framing',
        headerClass: `th-component-header align-center border-right th-vertical-bottom`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_framing_162')
            : getHighlightRange('one_knee_framing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: 'one_knee_csp',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Both<br />Up',
        field: 'other_csp',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'knee_percent', { removePercent: true }),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_blocking_162' : 'one_knee_blocking',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_blocking_162')
            : getHighlightRange('one_knee_blocking'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_blocking_162' : 'other_blocking',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_blocking_162')
            : getHighlightRange('one_knee_blocking'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: 'one_knee_pbwp',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'Runners On<br />situations only',
        displayFn: (val) => guff(val, 'run_value_zone'),
    },
    {
        name: 'Both<br />Up',
        field: 'other_pbwp',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'Runners On<br />situations only',
        displayFn: (val) => guff(val, 'run_value_zone'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: isLeague ? 'one_knee_throwing_162' : 'one_knee_throwing',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_throwing_162')
            : getHighlightRange('one_knee_throwing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Both<br />Up',
        field: isLeague ? 'other_throwing_162' : 'other_throwing',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        highlight: isLeague
            ? getHighlightRange('one_knee_throwing_162')
            : getHighlightRange('one_knee_throwing'),
        toolTip: isLeague ? 'Per 24,000 Pitches<br />(Catcher-Season)' : '',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Knee(s)<br />Down',
        field: 'one_knee_csaa',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'run_value_zone'),
    },
    {
        name: 'Both<br />Up',
        field: 'other_csaa',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'run_value_zone'),
    },
    {
        name: 'Total<br />Runs',
        field: 'total_frv',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'run_value'),
    },
    {
        name: 'Untracked<br />Runs',
        field: 'total_untracked',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip: 'RV on pitches<br />without skeletal<br />tracking',
        displayFn: (val) => guff(val, 'run_value'),
    },
]

const initColspan = isLeague ? 3 : 4
const secondaryHeaders = [
    {
        name: '',
        colspan: initColspan + groupHeaders.length,
        headerClass: 'th-name-header',
    },
    {
        name: 'Usage (%)',
        colspan: 6,
        headerClass:
            'th-title-header border-left border-right th-vertical-bottom',
    },
    {
        name: 'Relative<br />to Batter (%)',
        colspan: 2,
        headerClass: 'th-title-header border-left border-right',
    },
    {
        name: `Framing<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-left',
    },
    {
        name: `Blocking<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header',
    },
    {
        name: `Throwing<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-right',
    },
    {
        name: 'Catching<br />Runs',
        colspan: 1,
        headerClass: 'th-title-header border-right',
    },
    {
        name: `Pitching<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-right',
    },
]

const secondaryHeadersExpanded = [
    {
        name: '',
        colspan: 4 + groupHeaders.length,
        headerClass: 'th-name-header',
    },
    {
        name: 'Usage (%)',
        colspan: 6,
        headerClass:
            'th-title-header border-left border-right th-vertical-bottom',
    },
    {
        name: `Framing<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-left th-vertical-bottom',
    },
    {
        name: 'Shadow<br />Called<br />Strike %',
        colspan: 2,
        headerClass: 'th-title-header',
    },
    {
        name: `Blocking<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-left th-vertical-bottom',
    },
    {
        name: 'PBWP per<br />100 pitches',
        colspan: 2,
        headerClass: 'th-title-header th-vertical-bottom',
    },
    {
        name: `Throwing<br />Runs${isLeague ? ' / Season' : ''}`,
        colspan: 2,
        headerClass: 'th-title-header border-left th-vertical-bottom',
    },
    {
        name: 'CS RAA per<br />100 SBA',
        colspan: 2,
        headerClass: 'th-title-header border-right th-vertical-bottom',
    },
    {
        name: 'Catching<br />Runs',
        colspan: 2,
        headerClass: 'th-title-header border-right th-vertical-bottom',
    },
]

function loadTable(
    data,
    existingSort = null,
    existingSortDir = null,
    expandedCols = false
) {
    if (showLeagueAverage) {
        // eslint-disable-next-line no-param-reassign
        data = [
            ...data,
            ...leagueData.map((d) => ({
                ...d,
                pinned: true,
            })),
        ]
    }

    const width = document.getElementById(tableDivId).offsetWidth
    let repeatHeader = false
    if (width <= 768) {
        repeatHeader = 15
    }

    const defaultSort =
        existingSort || serverParams.sortColumn || 'pct_one_down'
    const defaultSortDirection =
        existingSortDir || serverParams.sortDirection || 'desc'

    const actualHeaders = expandedCols ? headersExpanded : headers

    // eslint-disable-next-line no-new
    new Table({
        id: tableDivId,
        repeatHeader,
        headers: concatGroupedHeaders(actualHeaders, groupHeaders),
        secondaryHeaders: expandedCols
            ? secondaryHeadersExpanded
            : secondaryHeaders,
        highlightFalsy: true,
        data,
        uniqueId: 'id',
        hasPinned: true,
        defaultSort,
        defaultSortDirection,
        sortClass: 'column-sort',
        showRank: true,
        sortCallback(column, _, { state }) {
            const url = new URL(window.location)
            url.searchParams.set('sortColumn', column)
            url.searchParams.set('sortDirection', state)
            window.history.replaceState('', '', url.toString())
        },
    })

    initStaticHeaders(tableDivId, data)
}

const $input = document.querySelector('.table-search')
let currentData = data

if (data && data.length) {
    loadTable(data)

    if ($input) {
        $input.addEventListener('input', (e) => {
            currentData = getFilteredData(data, e.target.value)
            loadTable(currentData)

            const isSearchEmpty = e.target.value.length === 0
            let uniqueFilteredIDs = []

            if (!isSearchEmpty) {
                const filteredIDs = currentData.map((d) => d.id)
                uniqueFilteredIDs = [...new Set(filteredIDs)]
            }

            viz.updateDataFromSearch(uniqueFilteredIDs, isSearchEmpty)
        })
    }
}

document.getElementById('btn-update').addEventListener('click', () => {
    window.location = populateUrl(
        {
            minPitches: document.getElementById('ddlMinPitches')?.value,
            minResults: document.getElementById('ddlMinResults')?.value,
            kneeCode: document.getElementById('ddlKneeCode')?.value,
        },
        ['groupBy']
    )
})

const $leagueAverageToggle = document.getElementById('btnLeagueAvg')

$leagueAverageToggle?.addEventListener('click', () => {
    showLeagueAverage = !showLeagueAverage
    const currentParams = new URLSearchParams(window.location.search)
    loadTable(
        currentData,
        currentParams.get('sortColumn'),
        currentParams.get('sortDirection')
    )
})

document.getElementById('btn-columns').addEventListener('click', () => {
    expanded = !expanded
    const currentParams = new URLSearchParams(window.location.search)
    document.getElementById('btn-columns').innerText = expanded
        ? 'Show Fewer Columns'
        : 'Show More Columns'
    loadTable(
        currentData,
        currentParams.get('sortColumn'),
        currentParams.get('sortDirection'),
        expanded
    )
})
